const SALT_RE = /\b(calcium|sodium|potassium|magnesium|hydrochloride|hcl|sulfate|sulphate|maleate|phosphate|tartrate|acetate|citrate|bromide|chloride|mesylate|fumarate|succinate|besylate|trihydrate|dihydrate|monohydrate|amorphous|propylene glycol solvate)\b/gi;

const JUNK_RE = /\b(film coated|coated|extended release|delayed release|orally disintegrating|for injection|injectable|oral|tablet|tablets|capsule|capsules|solution|suspension|chewable|powder|granule|usp)\b/gi;

const DOSE_RE = /\b\d+(\.\d+)?\s*(mg|mcg|ug|g|ml|iu|units?|%)\b/gi;

const RELEASE_RE = /\b(er|xr|sr|cr|dr|ir|odt|ec|la|xl)\b/gi;

const ABBR = {
  hctz: 'hydrochlorothiazide',
  hct: 'hydrochlorothiazide',
};

const KEEP_UPPER = new Set(['XR', 'ER', 'SR', 'CR', 'DR', 'IR', 'HCT', 'HCTZ', 'XL', 'LA', 'ODT', 'EC']);

export function titleCaseName(s) {
  if (!s) return '';
  return s
    .split(/(\s+|-)/)
    .map((part) => {
      const upper = part.toUpperCase();
      if (KEEP_UPPER.has(upper)) return upper;
      if (!/[a-z]/i.test(part)) return part;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join('');
}

export function groupKey(raw) {
  let s = String(raw || '');
  s = s.replace(/\(.*?\)/g, ' ');
  s = s.replace(/-/g, ' ');
  s = s.replace(/[/+,]/g, ' and ');
  s = s.toLowerCase();
  s = s.replace(SALT_RE, ' ');
  s = s.replace(JUNK_RE, ' ');
  s = s.replace(DOSE_RE, ' ');
  s = s.replace(RELEASE_RE, ' ');
  s = s.replace(/[^a-z0-9 +]+/g, ' ');
  s = s.replace(/\s+/g, ' ').trim();
  s = s.replace(/^(and\s+)+|(and\s*)+$/g, '').trim();
  const parts = s
    .split(/\s+and\s*/)
    .map((p) => p.trim())
    .filter((p) => p && p !== 'and')
    .map((p) => ABBR[p] || p);
  return [...new Set(parts)].sort().join('/');
}

export function displayNameFromKey(key) {
  if (!key) return '';
  return key.split('/').map(titleCaseName).join(' / ');
}

export function isDistinctBrand(brand, gkey) {
  const bk = groupKey(brand);
  if (!bk || !gkey) return false;
  if (bk === gkey) return false;
  const bparts = bk.split('/');
  if (bparts.length !== 1) return false;
  if (gkey.split('/').includes(bk)) return false;
  return true;
}

export function humanizeForm(form) {
  if (!form) return '';
  const lower = form.toLowerCase();
  const map = [
    [/tablet.*extended release/, 'Tablet (extended release)'],
    [/capsule.*extended release/, 'Capsule (extended release)'],
    [/tablet.*delayed/, 'Tablet (delayed release)'],
    [/tablet.*film coated/, 'Tablet'],
    [/tablet.*coated/, 'Tablet'],
    [/tablet/, 'Tablet'],
    [/capsule/, 'Capsule'],
    [/inject/, 'Injection'],
    [/solution|liquid/, 'Liquid'],
    [/suspension/, 'Liquid (suspension)'],
    [/cream/, 'Cream'],
    [/ointment/, 'Ointment'],
    [/gel/, 'Gel'],
    [/patch|transdermal/, 'Patch'],
    [/spray/, 'Spray'],
    [/inhal/, 'Inhalation'],
    [/drop/, 'Drops'],
    [/powder/, 'Powder'],
    [/granule/, 'Granule'],
    [/lozenge/, 'Lozenge'],
    [/chewable/, 'Chewable tablet'],
  ];
  for (const [re, label] of map) {
    if (re.test(lower)) return label;
  }
  return titleCaseName(form.replace(/,/g, ' '));
}

export function parseStrength(raw) {
  if (!raw) return null;
  const text = String(raw).trim();
  if (/\bkg\b/i.test(text)) return null;
  const m = text.match(/^([\d.]+)\s*([a-zA-Zµμ]+)\s*(?:\/\s*([\d.]*)\s*([a-zA-Zµμ]*))?/);
  if (!m) return null;
  const amount = parseFloat(m[1]);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  let unit = (m[2] || 'mg').toLowerCase();
  if (unit === 'µg' || unit === 'μg' || unit === 'ug') unit = 'mcg';
  const denomAmt = m[3] ? parseFloat(m[3]) : null;
  const denomUnit = (m[4] || '').toLowerCase();
  if (denomAmt && denomUnit && denomUnit !== '1' && denomAmt !== 1) {
    return {
      amount,
      unit,
      label: `${formatNum(amount)} ${unit} / ${formatNum(denomAmt)} ${denomUnit}`,
      sortValue: amount,
    };
  }
  return {
    amount,
    unit,
    label: `${formatNum(amount)} ${unit}`,
    sortValue: amount,
  };
}

function formatNum(n) {
  return Number.isInteger(n) ? String(n) : String(n);
}

export function comboStrengthLabel(ingredients, primaryNames) {
  const parsed = [];
  for (const ing of ingredients || []) {
    const key = groupKey(ing.name);
    if (primaryNames && primaryNames.length && !primaryNames.includes(key)) continue;
    const p = parseStrength(ing.strength);
    if (p) parsed.push({ key, ...p });
  }
  if (!parsed.length) return null;
  parsed.sort((a, b) => a.key.localeCompare(b.key));
  return {
    label: parsed.map((p) => p.label).join(' / '),
    amount: parsed[0].amount,
    unit: parsed[0].unit,
    sortValue: parsed.reduce((s, p) => s + p.sortValue, 0),
  };
}

export function unitFromStrength(strength) {
  if (!strength?.unit) return 'mg';
  const u = strength.unit.toLowerCase();
  if (['mg', 'mcg', 'g', 'ml', 'units'].includes(u)) return u;
  if (u === 'mcg' || u === 'ug') return 'mcg';
  return 'mg';
}

export function timesForFrequency(frequency, existing = []) {
  const f = (frequency || '').toLowerCase();
  let count = 1;
  if (f.includes('as needed')) count = 0;
  else if (f.includes('twice') || f.includes('12 hour')) count = 2;
  else if (f.includes('three') || f.includes('8 hour')) count = 3;
  else if (f.includes('four') || f.includes('4 hour')) count = 4;
  else if (f.includes('6 hour')) count = 3;
  else if (f.includes('weekly') || f.includes('monthly') || f.includes('bi-weekly')) count = 1;

  const defaults = ['08:00', '14:00', '20:00', '22:00'];
  const next = [];
  for (let i = 0; i < count; i += 1) {
    next.push(existing[i] || defaults[i] || '08:00');
  }
  return next;
}

export function sanitizeQuery(query) {
  return String(query || '')
    .replace(/[:"()[\]{}\\+]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function rankMedicationChoice(choice, query) {
  const q = query.toLowerCase();
  const name = (choice.name || '').toLowerCase();
  const brands = (choice.brands || []).map((b) => b.toLowerCase());
  let score = 0;
  if (name === q) score += 200;
  if (name.startsWith(q)) score += 120;
  if (name.includes(q)) score += 50;
  for (const b of brands) {
    if (b === q) score += 180;
    else if (b.startsWith(q)) score += 90;
    else if (b.includes(q)) score += 35;
  }
  score += Math.min(choice.recordCount || 0, 25);
  score -= (choice.name.match(/\//g) || []).length * 8;
  return score;
}

export function aggregateMedicationResults({ query, genericCounts, brandCounts, records }) {
  const groups = new Map();

  const ensure = (key) => {
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        name: displayNameFromKey(key),
        brands: new Map(),
        genericTerms: new Set(),
        forms: new Map(),
        recordCount: 0,
      });
    }
    return groups.get(key);
  };

  for (const row of genericCounts || []) {
    const key = groupKey(row.term);
    if (!key) continue;
    const g = ensure(key);
    g.genericTerms.add(row.term);
    g.recordCount += row.count || 0;
  }

  const brandToKey = new Map();

  for (const rec of records || []) {
    const key = groupKey(rec.generic_name || rec.brand_name || '');
    if (!key) continue;
    const g = ensure(key);
    if (rec.generic_name) g.genericTerms.add(rec.generic_name);
    const brand = rec.brand_name || rec.brand_name_base || '';
    if (brand && isDistinctBrand(brand, key)) {
      g.brands.set(brand, (g.brands.get(brand) || 0) + 1);
      brandToKey.set(brand.toLowerCase(), key);
    }
    if (rec.dosage_form) {
      const form = humanizeForm(rec.dosage_form);
      g.forms.set(form, (g.forms.get(form) || 0) + 1);
    }
  }

  const unresolvedBrands = [];
  for (const row of brandCounts || []) {
    const term = row.term;
    if (!term) continue;
    const mapped = brandToKey.get(term.toLowerCase());
    if (mapped && isDistinctBrand(term, mapped)) {
      const g = groups.get(mapped);
      g.brands.set(term, (g.brands.get(term) || 0) + (row.count || 0));
      continue;
    }
    const asGeneric = groupKey(term);
    if (!asGeneric) continue;
    // "Atorvastatin Calcium" is the generic restated as a brand field — skip.
    if (groups.has(asGeneric) && !isDistinctBrand(term, asGeneric)) continue;
    // Combination products listed in brand_name are their own groups, not brands.
    if (asGeneric.includes('/')) continue;
    if (term.trim().split(/\s+/).length > 4) continue;
    unresolvedBrands.push(row);
  }

  return { groups, unresolvedBrands };
}

export function groupsToChoices(groups, query) {
  const choices = [];
  for (const g of groups.values()) {
    if (!g.key) continue;
    const brandList = [...g.brands.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => titleCaseName(name));
    const uniqueBrands = [...new Set(brandList)];
    const forms = [...g.forms.entries()].sort((a, b) => b[1] - a[1]).map(([name]) => name);
    const choice = {
      id: g.key,
      name: g.name,
      commonBrand: uniqueBrands[0] || '',
      brands: uniqueBrands,
      genericTerms: [...g.genericTerms].filter(Boolean),
      formsPreview: forms.slice(0, 3),
      recordCount: g.recordCount,
    };
    choice.score = rankMedicationChoice(choice, query);
    choices.push(choice);
  }
  choices.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
  return choices.slice(0, 8);
}

export function genericKeyFromRecord(rec) {
  if (!rec) return '';
  const fromName = groupKey(rec.generic_name || '');
  const fromIng = [...new Set((rec.active_ingredients || []).map((i) => groupKey(i.name)).filter(Boolean))].sort();
  const ingKey = fromIng.join('/');
  if (ingKey && (!fromName || fromName === groupKey(rec.brand_name || '') || fromIng.length > 1)) {
    return ingKey;
  }
  return fromName || ingKey;
}

export function attachBrandToGroups(groups, brandTerm, genericName, count = 1) {
  const key = groupKey(genericName || brandTerm);
  if (!key) return;
  if (!groups.has(key)) {
    groups.set(key, {
      key,
      name: displayNameFromKey(key),
      brands: new Map(),
      genericTerms: new Set(),
      forms: new Map(),
      recordCount: 0,
    });
  }
  const g = groups.get(key);
  if (genericName) g.genericTerms.add(genericName);
  if (isDistinctBrand(brandTerm, key)) {
    g.brands.set(brandTerm, (g.brands.get(brandTerm) || 0) + count);
  }
}
